import { useState, useEffect, useRef } from "react";
import CommonLayout from "./commonLayout";
import { Col, Container, Row } from "react-bootstrap";
import { GuestTeamTable } from "@/components/table/index";
import { useParams, Navigate } from "react-router-dom";
import { decrypt } from "@/utils/crypto";
import { Group } from "@/components";
import { WelcomeParticipants } from "../components/modal";

function GuestParticipants({ socket, isLoggedIn }) {
  const { id, dynamicPart } = useParams();
  const [data, setData] = useState([]);
  const [teamData, setTeamData] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const teamRef = useRef(teamData);
  const sessionData = data || {};
  const session = sessionData?.session || {};

  const fetchTeamsData = async () => {
    try {
      if (id && socket) {
        // Remove existing listener to prevent duplicates (fix for race condition)
        socket.off("fetch-team-data");

        // Register listener BEFORE emitting (ensures listener is ready when response arrives)
        socket.on("fetch-team-data", (response) => {
          if (response.status === true) {
            getGroupTeamGameData(response.data);
          }
          socket.off("fetch-team-data");
        });

        socket.emit("fetch-team-data", {
          session_id: Number(decrypt(id)),
        });
      }
    } catch (error) {}
  };
  const fetchData = async () => {
    try {
      if (id && socket) {
        // Remove existing listener to prevent duplicates (fix for race condition)
        socket.off("fetch-single-session-data");

        // Register listener BEFORE emitting (ensures listener is ready when response arrives)
        socket.on("fetch-single-session-data", (response) => {
          if (
            response.status === true &&
            Number(response.data.session.id) === Number(decrypt(id))
          ) {
            setData(response.data);
          }
          socket.off("fetch-single-session-data");
        });

        socket.emit("fetch-single-session-data", {
          id: Number(decrypt(id)),
        });
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchTeamsData();
    fetchData();
    const fetchRoleData = async () => {
      socket.off("fetch-role-list");
      socket.on("fetch-role-list", (response) => {
        if (response.status === true) {
          setRoleList(response.data || { roles: [] });
        }
        socket.off("fetch-role-list");
      });
      socket.emit("fetch-role-list");
    };
    fetchRoleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    teamRef.current = teamData;
  }, [teamData]);

  const getGroupTeamGameData = async (teamDetails) => {
    try {
      const data = teamDetails || [];

      if (data.length > 0) {
        const teamIds = data
          .filter((item) => Number(item?.current_status) === 5)
          .map((item) => item?.id)
          .filter(Boolean);

        if (teamIds.length === 0) {
          setTeamData(data.map((team) => ({ ...team, games: [] })));
          return;
        } else {
          setTeamData((prevTeams) => {
            if (prevTeams.length > 0) {
              return prevTeams.map((team) => {
                const teamId = team?.team_id || team?.id;
                const updatedTeam = data.find(
                  (t) => (t?.team_id || t?.id) === teamId,
                );
                return {
                  ...team,
                  ...(updatedTeam || {}),
                };
              });
            }
            // Reload case: prevTeams was empty; set full team list with games from data
            return data.map((team) => ({
              ...team,
            }));
          });
        }
      }
    } catch (error) {
      return [];
    }
  };

  const handleSocket = (sessionId) => {
    socket.emit("joinSessionRoom", sessionId);
    const handleResJoinSessionRoom = () => {
      socket.emit("register-session-subscribe", {
        session_id: sessionId,
        isStartGameCount: 0,
      });
    };
    socket.on("resJoinSessionRoom", handleResJoinSessionRoom);
    return () => {
      socket.off("resJoinSessionRoom", handleResJoinSessionRoom);
    };
  };
  let isGameComplete =
    (teamData?.length > 0 &&
      teamData.every((team) => team.is_game_complete === 1)) ||
    session?.is_game_complete === 1;

  useEffect(() => {
    const sessionId = Number(decrypt(id));
    const cleanupResJoinSessionRoom = handleSocket(sessionId);
    const handleConnectionEstablished = () => {
      if (["participants"].includes(dynamicPart) && !isLoggedIn) {
        handleSocket(sessionId);
      }
    };
    const handleResRegisterSessionSubscribe = (response) => {
      getGroupTeamGameData(response.teams);
    };
    socket.on("connectionEstablished", handleConnectionEstablished);
    socket.on(
      "res-register-session-subscribe",
      handleResRegisterSessionSubscribe,
    );
    socket.on("res-team-participants", (response) => {
      let hasRoundChange = false;
      if (response.team && response.team.length > 0) {
        const latestTeams = teamRef.current ?? [];
        hasRoundChange = response.team.some((updatedTeam) => {
          const prevTeam = latestTeams.find((t) => t.id === updatedTeam.id);
          return (
            prevTeam != null &&
            updatedTeam.current_round !== undefined &&
            prevTeam.current_round !== updatedTeam.current_round
          );
        });
      }

      setTeamData((prevTeams) => {
        return (
          prevTeams?.map((team) => {
            // Filter participants for this team only
            const teamParticipantsForThisTeam =
              response.teamParticipants.filter(
                (item) => item.team_id === team.id,
              );
            const updatedData =
              teamParticipantsForThisTeam.length > 0
                ? teamParticipantsForThisTeam[0]
                : null;
            const updatedParticipant = teamParticipantsForThisTeam.map(
              (item) => {
                const participant = team?.participant?.find(
                  (p) => p.id === item.id,
                );
                if (participant) {
                  return {
                    ...participant,
                    participant_id: item.participant ?? null,
                    current_status: item.current_status,
                    is_game_leave: item.is_game_leave,
                  };
                }
                return {
                  ...item,
                  participant_id: item.participant ?? null,
                  current_status: item.current_status,
                  is_game_leave: item.is_game_leave,
                };
              },
            );
            if (updatedData) {
              const teamStatus = response.team?.find(
                (item) => item.id === team.id,
              )?.current_status;
              return {
                ...team,
                current_status: teamStatus,
                team_participant: updatedData.team_participant,
                participant: updatedParticipant,
              };
            }
            return team;
          }) || []
        );
      });
      fetchData();
      // If round changed, also refresh games data for accurate activity display
      if (hasRoundChange && response.team) {
        getGroupTeamGameData(response.team);
      }
    });
    // Got latest game data when move to next round
    socket.on("res-session-game-data", (response) => {
      const participants = response.teamParticipants;

      const formattedParticipants = participants.map((p) => ({
        id: p.id,
        role_id: p.role,
        session_id: p.session_id,
        is_game_leave: p.is_game_leave,
        current_status: p.current_status,
        participant_id: p.participant,
        is_help_call_for_facilitator: p.is_help_call_for_facilitator,
      }));

      const updatedTeamData = response.team.map((t) => ({
        ...t,
        participant: formattedParticipants,
      }));

      getGroupTeamGameData(updatedTeamData);
    });
    // when i change the team data
    socket.on("res-update-team-record", (response) => {
      setTeamData((prevTeams) => {
        return (
          prevTeams?.map((team) => {
            const updatedTeam = response.data.find((t) => t.id === team.id);
            if (updatedTeam) {
              return { ...team, ...updatedTeam };
            }
            return team;
          }) || []
        );
      });
    });
    socket.on("res-get-session-details", (response) => {
      if (
        response.success === true &&
        response.data &&
        Number(response.data.id) === Number(decrypt(id))
      ) {
        setData((prevState) => ({
          ...prevState,
          session: response.data,
        }));
      }
    });
    socket.on("update-session-data", (response) => {
      if (
        response.status === true &&
        Number(response.data.session.id) === Number(decrypt(id))
      ) {
        setData((prevState) => ({
          ...prevState,
          session: response.data.session,
        }));
      }
    });
    socket.on("updatepractice-run-session-data", (response) => {
      if (
        response.status === true &&
        Number(response.data.session.id) === Number(decrypt(id))
      ) {
        setData((prevState) => ({
          ...prevState,
          session: response.data.session,
        }));
      }
    });
    socket.on("game-completed", async (response) => {
      if (Number(response.session_id) === Number(decrypt(id))) {
        await fetchTeamsData();
      }
    });
    return () => {
      cleanupResJoinSessionRoom();
      socket.off("connectionEstablished", handleConnectionEstablished);
      socket.off(
        "res-register-session-subscribe",
        handleResRegisterSessionSubscribe,
      );
      socket.off("res-team-participants");
      socket.off("res-get-session-details");
      socket.off("update-session-data");
      socket.off("updatepractice-run-session-data");
      socket.off("game-completed");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!socket) return;

    const sessionId = Number(decrypt(id));
    let hasFetched = false; // guard to avoid multiple fetchData calls

    const handleCheck = async () => {
      socket.emit("isInRoom", `${sessionId}`, async (isInRoom) => {
        if (!isInRoom && !hasFetched) {
          socket.emit("joinSessionRoom", sessionId);
          await fetchData();
          hasFetched = true;

          // reset guard after a short delay so future disconnects can trigger again
          setTimeout(() => {
            hasFetched = false;
          }, 3000);
        }
      });
    };

    // Initial check
    handleCheck();

    // Run every 5s
    const intervalRoom = setInterval(handleCheck, 5000);

    // Reset on reconnect
    socket.on("connect", () => {
      hasFetched = false;
      handleCheck();
    });

    return () => {
      clearInterval(intervalRoom);
      socket.off("connect");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  if (!["participants", "teams", "group"].includes(dynamicPart)) {
    return <Navigate to={`/participants/${id}`} replace />;
  }

  return (
    <>
      <CommonLayout guestParticipants={true} sessionData={sessionData} />
      <Container className="mt-10">
        <Row className="gx-lg-20">
          {isGameComplete ? (
            <Group
              teamData={teamData}
              sessionData={sessionData}
              socket={socket}
              guestParticipants={true}
            />
          ) : (
            teamData?.map((item, index) => {
              return (
                <Col lg={6} key={index}>
                  <GuestTeamTable
                    socket={socket}
                    allTeam={teamData}
                    sessionData={sessionData}
                    teamData={item}
                    current_status={item?.current_status}
                    current_round={item?.current_round}
                    roleList={roleList}
                  />
                </Col>
              );
            })
          )}
          {!teamData?.length && (
            <Col lg={6}>
              <GuestTeamTable socket={socket} />
            </Col>
          )}
        </Row>
      </Container>
      <WelcomeParticipants id={id} session={session} socket={socket} />
    </>
  );
}

export default GuestParticipants;
